<template>
    <div v-if="show" class="notes-overlay" @pointerdown.self="closeClick">
        <div class="notes-bar">
            <div class="notes-bar-title"><i class="fa-solid fa-note-sticky"></i> {{ $LANG.Get('labels.notesTitle') }}</div>
            <NevsButton class="primary" @click="addNote">
                <i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addNote') }}
            </NevsButton>
            <NevsButton class="error" @click="closeClick">
                <i class="fa-solid fa-xmark"></i> {{ $LANG.Get('buttons.close') }}
            </NevsButton>
        </div>

        <div ref="canvas" class="notes-canvas">
            <div v-if="notes.length === 0" class="notes-empty">{{ $LANG.Get('labels.noNotes') }}</div>

            <div v-for="note in notes" :key="note.id" :class="'note note-' + note.color"
                 :style="noteStyle(note)">
                <div class="note-grip" @pointerdown="dragStart($event, note)">
                    <i class="fa-solid fa-grip"></i>
                    <span class="note-colors">
                        <span v-for="color in colors" :key="color"
                              :class="['note-color', 'note-color-' + color, {'note-color-selected': note.color === color}]"
                              :title="$LANG.Get('labels.noteColor')"
                              @pointerdown.stop @click="setColor(note, color)"></span>
                    </span>
                    <span v-if="noteInfoTitle(note) !== ''" :title="noteInfoTitle(note)" class="note-info"
                          @pointerdown.stop><i class="fa-solid fa-circle-info"></i></span>
                    <span :title="$LANG.Get('tooltips.delete')" class="note-delete"
                          @pointerdown.stop @click="deleteNote(note)"><i class="fa-solid fa-trash"></i></span>
                </div>
                <textarea v-model="note.content" class="note-text" :maxlength="contentMaxLength"
                          @focus="focusedNoteId = note.id" @blur="contentBlur(note)"
                          @input="contentInput(note)"></textarea>
                <div class="note-footer">
                    <span class="note-counter">{{ note.content.length }}/{{ contentMaxLength }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import NevsButton from "@/components/nevs/NevsButton.vue";

// One note's footprint on the board. Fixed, so a note never reflows under the person writing in it, and so
// the spawn offset and the viewport clamp have something definite to work with.
const NOTE_WIDTH = 240;
const NOTE_HEIGHT = 200;
// Where the first note lands, and how far each following one is nudged when the spot is taken.
const SPAWN_ORIGIN = 24;
const SPAWN_OFFSET = 28;
// How long typing has to stop before the note is saved. Long enough that a sentence is one request.
const SAVE_DEBOUNCE_MS = 600;
// The board is re-read this often while the overlay is open; there are no live updates.
const POLL_INTERVAL_MS = 10000;

export default {
    name: "NotesOverlay",
    components: {NevsButton},
    props: {
        show: {
            type: Boolean,
            default: false
        }
    },
    emits: ['done'],
    data() {
        return {
            notes: [],
            // Keep in sync with NoteController::COLORS.
            colors: ['yellow', 'green', 'blue', 'pink', 'orange', 'grey'],
            contentMaxLength: 255,
            // The note the cursor is in. A poll never overwrites it, so nobody has text pulled out from under
            // them mid-sentence.
            focusedNoteId: null,
            // The note being dragged: also left alone by a poll, and drawn above the others.
            draggedNoteId: null,
            pollTimer: null
        }
    },
    watch: {
        show() {
            if (this.show) {
                this.load();
                this.startPolling();
            } else {
                this.stopPolling();
                this.focusedNoteId = null;
                this.draggedNoteId = null;
            }
        }
    },
    methods: {
        closeClick() {
            this.$emit('done');
        },
        load() {
            let vm = this;
            // Silent: the board reloads every ten seconds and the full-screen loader would flash each time.
            this.$API.APICall('get', 'notes', {}, (data, success) => {
                if (success) {
                    vm.mergeNotes(data.records);
                }
            }, false);
        },
        /**
         * Takes the board as the server has it, but keeps the local copy of the note being typed in or dragged.
         * Those two are the only places where the user's own state is newer than the server's.
         */
        mergeNotes(records) {
            let vm = this;
            this.notes = records.map((record) => {
                let keep = (record.id === vm.focusedNoteId || record.id === vm.draggedNoteId)
                    ? vm.notes.find((note) => note.id === record.id)
                    : null;
                return keep !== undefined && keep !== null ? keep : record;
            });
        },
        startPolling() {
            this.stopPolling();
            this.pollTimer = setInterval(this.load, POLL_INTERVAL_MS);
        },
        stopPolling() {
            if (this.pollTimer !== null) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        /**
         * The first free spot on the board: two notes are never dropped exactly on top of each other, so a new
         * one is always visible instead of hiding under the last.
         */
        spawnPosition() {
            let x = SPAWN_ORIGIN;
            let y = SPAWN_ORIGIN;
            let canvas = this.$refs.canvas;
            let maxY = canvas ? canvas.clientHeight - NOTE_HEIGHT : 0;
            while (this.notes.some((note) => note.x === x && note.y === y)) {
                x += SPAWN_OFFSET;
                y += SPAWN_OFFSET;
                // Back to the top once the diagonal runs off the board, shifted sideways so it is still a free row.
                if (maxY > 0 && y > maxY) {
                    y = SPAWN_ORIGIN;
                    x += NOTE_WIDTH;
                }
            }
            return {x: x, y: y};
        },
        addNote() {
            let vm = this;
            let position = this.spawnPosition();
            this.$API.APICall('post', 'notes', {x: position.x, y: position.y}, (data, success) => {
                if (success) {
                    vm.load();
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        deleteNote(note) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (!response) return;
                    if (vm.focusedNoteId === note.id) vm.focusedNoteId = null;
                    vm.$API.APICall('delete', 'notes/' + note.id, {}, (data, success) => {
                        if (success) {
                            vm.load();
                        } else {
                            vm.$HELPERS.HandleServerError(vm, data);
                        }
                    });
                }
            });
        },
        setColor(note, color) {
            note.color = color;
            this.save(note, {color: color});
        },
        contentInput(note) {
            // Debounced, so a sentence is one request rather than one per keystroke. The timer lives on the
            // note object itself, so two notes edited in turn cannot cancel each other's save.
            if (note.saveTimer) clearTimeout(note.saveTimer);
            let vm = this;
            note.saveTimer = setTimeout(() => {
                note.saveTimer = null;
                vm.save(note, {content: note.content});
            }, SAVE_DEBOUNCE_MS);
        },
        contentBlur(note) {
            // Leaving the note saves it straight away rather than waiting out the debounce, and only then is it
            // released to the poll.
            if (note.saveTimer) {
                clearTimeout(note.saveTimer);
                note.saveTimer = null;
                this.save(note, {content: note.content});
            }
            if (this.focusedNoteId === note.id) this.focusedNoteId = null;
        },
        save(note, payload) {
            let vm = this;
            this.$API.APICall('put', 'notes/' + note.id, payload, (data, success) => {
                if (!success) {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            }, false);
        },
        dragStart(event, note) {
            let canvas = this.$refs.canvas;
            if (!canvas) return;
            let rect = canvas.getBoundingClientRect();
            let position = this.clampedPosition(note);
            this.draggedNoteId = note.id;
            this.dragOffsetX = event.clientX - rect.left - position.x;
            this.dragOffsetY = event.clientY - rect.top - position.y;
            // Pointer events rather than mouse events: reception runs on tablets, where mousemove never fires.
            event.currentTarget.setPointerCapture(event.pointerId);
            event.currentTarget.addEventListener('pointermove', this.dragMove);
            event.currentTarget.addEventListener('pointerup', this.dragEnd);
            event.currentTarget.addEventListener('pointercancel', this.dragEnd);
            event.preventDefault();
        },
        dragMove(event) {
            let note = this.notes.find((candidate) => candidate.id === this.draggedNoteId);
            let canvas = this.$refs.canvas;
            if (!note || !canvas) return;
            let rect = canvas.getBoundingClientRect();
            note.x = this.clamp(event.clientX - rect.left - this.dragOffsetX, 0, canvas.clientWidth - NOTE_WIDTH);
            note.y = this.clamp(event.clientY - rect.top - this.dragOffsetY, 0, canvas.clientHeight - NOTE_HEIGHT);
        },
        dragEnd(event) {
            event.currentTarget.removeEventListener('pointermove', this.dragMove);
            event.currentTarget.removeEventListener('pointerup', this.dragEnd);
            event.currentTarget.removeEventListener('pointercancel', this.dragEnd);
            let note = this.notes.find((candidate) => candidate.id === this.draggedNoteId);
            this.draggedNoteId = null;
            // Saved on drop, not while moving: a drag is one request, whatever route it took.
            if (note) this.save(note, {x: Math.round(note.x), y: Math.round(note.y)});
        },
        clamp(value, min, max) {
            if (max < min) return min;
            return Math.min(Math.max(value, min), max);
        },
        /**
         * Where the note is actually drawn. Stored coordinates are pulled back into the board, so a note left
         * at the far edge of a large monitor is still reachable on a small one. The stored value is not
         * touched: it is only rewritten when someone actually drags the note.
         */
        clampedPosition(note) {
            let canvas = this.$refs.canvas;
            if (!canvas) return {x: note.x, y: note.y};
            return {
                x: this.clamp(note.x, 0, canvas.clientWidth - NOTE_WIDTH),
                y: this.clamp(note.y, 0, canvas.clientHeight - NOTE_HEIGHT)
            };
        },
        noteStyle(note) {
            let position = this.clampedPosition(note);
            return {
                left: position.x + 'px',
                top: position.y + 'px',
                width: NOTE_WIDTH + 'px',
                height: NOTE_HEIGHT + 'px',
                // The note being dragged rides above the rest, so it is never lost under one it crosses.
                zIndex: note.id === this.draggedNoteId ? 20 : 10
            };
        },
        /**
         * Who wrote the note and who last changed its text, one per line. Shown on hover over the information
         * icon rather than on the note itself, so the note is all text and the board stays readable.
         */
        noteInfoTitle(note) {
            let lines = [];
            if (note.author_display) {
                lines.push(this.$LANG.Get('labels.noteAuthor') + ': ' + note.author_display);
            }
            if (note.changed_display) {
                let changed = this.$LANG.Get('labels.noteChanged') + ': ' + note.changed_display;
                if (note.changed_at_display) changed += ' (' + note.changed_at_display + ')';
                lines.push(changed);
            }
            return lines.join('\n');
        }
    },
    unmounted() {
        this.stopPolling();
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.notes-overlay {
    position: fixed;
    inset: 0;
    /* Above every piece of app chrome (top bar and menu are 2-4), but below the popup (100) and the loader
       (200) - the delete confirmation and the loader have to show over the board, not under it. */
    z-index: 50;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    flex-direction: column;
}

.notes-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 15px;
    background: $white;
    box-shadow: $card-shadow;
}

.notes-bar-title {
    flex: 1;
    font-size: 1.1em;
    font-weight: 600;
    color: $ink-strong;
}

/* The board itself. Notes are placed against this box, so the stored coordinates mean the same thing
   whatever is above it. */
.notes-canvas {
    position: relative;
    flex: 1;
    overflow: hidden;
}

.notes-empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $white;
    font-size: 1.1em;
}

.note {
    position: absolute;
    display: flex;
    flex-direction: column;
    border-radius: 6px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
    overflow: hidden;
}

/* Dragging is on this strip alone, so text inside the note can still be selected normally. */
.note-grip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    cursor: move;
    background: rgba(0, 0, 0, 0.07);
    touch-action: none;
    color: $ink-muted;
}

.note-colors {
    flex: 1;
    display: flex;
    gap: 4px;
}

/* The information icon and the trash can sit together at the end of the strip. */
.note-grip > .note-info + .note-delete {
    margin-left: 2px;
}

.note-color {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    cursor: pointer;
    border: 1px solid rgba(0, 0, 0, 0.25);
}

.note-color-selected {
    outline: 2px solid $ink-strong;
    outline-offset: 1px;
}

.note-delete {
    cursor: pointer;
    color: $ink-muted;
}

.note-delete:hover {
    color: $danger;
}

.note-text {
    flex: 1;
    border: none;
    outline: none;
    resize: none;
    background: transparent;
    padding: 8px;
    font-family: inherit;
    font-size: 0.95em;
    line-height: 1.35;
    color: $ink-strong;
}

.note-footer {
    padding: 4px 8px 6px;
    font-size: 0.72em;
    color: $ink-faint;
}

.note-counter {
    flex-shrink: 0;
}

.note-info {
    cursor: help;
    color: $ink-muted;
}

.note-info:hover {
    color: $ink-strong;
}

/* The palette. Keep the names in step with NoteController::COLORS. */
.note-yellow, .note-color-yellow { background: #fdf0a5; }
.note-green, .note-color-green { background: #cdeeb6; }
.note-blue, .note-color-blue { background: #c3e2f5; }
.note-pink, .note-color-pink { background: #f7c9d8; }
.note-orange, .note-color-orange { background: #fbd6ae; }
.note-grey, .note-color-grey { background: #dfe1e5; }
</style>
